/* eslint-disable react/prop-types */
import {memo} from 'react'

import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import MotorMarkdownWrapper from '@adv-mt/mt-markdown-wrapper/lib/AdDetailsTc'
import {Caption, Headline1, Subhead1} from '@adv-mt/ui/typography'

import {BASE_CLASS as PAGE_BASE_CLASS} from '../../config.js'

const BASE_CLASS = `${PAGE_BASE_CLASS}-financingConditions`

// Same lender → literal-key mapping as the legacy SectionFinancingLegalTerms component
// (@adv-mt/mt-section-financing-legal-terms). Kept here rather than shared because
// each Detail variant owns its own panel implementation independently.
const getLenderLegalConditionsTitleLiteralKey = (financingPartners, lender) => {
  const titleLiteralKeyByLender = {
    [financingPartners.BBVA]: 'COMMON.FINANCING_TC_BBVA',
    [financingPartners.BMW_BANK]: 'COMMON.FINANCING_TC_BMW_BANK',
    [financingPartners.CAIXA]: 'COMMON.FINANCING_TC_CAIXA',
    [financingPartners.LENDROCK]: 'COMMON.FINANCING_TC_LENDROCK',
    [financingPartners.MOBILIZE]: 'COMMON.FINANCING_TC_MOBILIZE',
    [financingPartners.MOBILIZE_NISSAN]: 'COMMON.FINANCING_TC_MOBILIZE_NISSAN',
    [financingPartners.MOBILIZE_NISSAN_NSFS]: 'COMMON.FINANCING_TC_MOBILIZE_NISSAN',
    [financingPartners.SANTANDER]: 'COMMON.FINANCING_TC_SANTANDER',
    [financingPartners.SANTANDER_PRI]: 'COMMON.FINANCING_TC_SANTANDER',
    [financingPartners.STELLANTIS]: 'COMMON.FINANCING_TC_STELLANTIS',
    [financingPartners.VWFS]: 'COMMON.FINANCING_TC_VWFS',
    [financingPartners.CONFIA]: 'COMMON.FINANCING_TC_CONFIA'
  }
  return titleLiteralKeyByLender[lender] || ''
}

const FinancingConditions = ({financingData, seller = {}, showC2cFinancing = false}) => {
  const {domain, i18n} = useSuiContext()

  const config = domain.get('config')
  const financingLegalConditionsSectionId =
    config.get('COMMON').AD_DETAIL_PAGE_SECTIONS.AD_DETAIL_PAGE_SECTION_IDS.FINANCING_LEGAL_CONDITIONS
  const {DEALER_PACKS} = config.get('dealer')
  const {FINANCING_PARTNERS, LENDERS_LEGAL_CONDITIONS} = config.get('financing')

  const isDealerSubscription = Boolean(seller.isDealer) && seller.dealer?.pack === DEALER_PACKS.SUBSCRIPTION
  // Mirrors legacy's `hasFinancing`: a dealer-backed ad with an active financing offer attached.
  // Does NOT cover the C2C (private-seller) financing flow — that's `showC2cFinancing`, handled separately.
  const hasActiveDealerFinancingOffer = Boolean(seller.dealer) && Boolean(financingData?.id)
  const shouldShowFinancingPanel = hasActiveDealerFinancingOffer || showC2cFinancing

  if (isDealerSubscription || !shouldShowFinancingPanel) {
    return null
  }

  const {lender, lenderLegalTextId} = financingData || {}

  const isSantanderPersonalLoan = lender === FINANCING_PARTNERS.SANTANDER_PRI
  const hasLenderLegalConditions = LENDERS_LEGAL_CONDITIONS.includes(lender)
  const lenderLegalConditionsTitleLiteralKey = getLenderLegalConditionsTitleLiteralKey(FINANCING_PARTNERS, lender)
  const panelTitleLiteralKey = isSantanderPersonalLoan
    ? 'COMMON.TERMS_CONDITIONS_LEGAL_PERSONAL_LOAN'
    : 'COMMON.FINANCING_TC'

  const formatFinancingNumber = value => {
    if (isNaN(value)) return ''
    return value < 0 ? '' : Intl.NumberFormat('de-DE', {minimumFractionDigits: 2}).format(value)
  }

  const getCommissionTypeText = ({isOpeningExpensesFinanced}) =>
    isOpeningExpensesFinanced ? i18n.t('COMMON.FINANCED_FEM').toLowerCase() : i18n.t('COMMON.CASH').toLowerCase()

  const markdownParser = html => {
    const {
      casualtyInsuranceAmount,
      dateFirstInstallment,
      dateOfferValidUntil,
      dateOfferEnd,
      dateSignup,
      fee,
      firstFee,
      insuranceRate,
      isOpeningExpensesFinanced,
      lifeInsuranceAmount,
      openingExpenses,
      openingFee,
      openingPercentage,
      minEntry,
      tae,
      terms,
      tin,
      totalAmountDue,
      totalInsuranceAmount,
      totalInterest,
      totalLoanAmount,
      totalLoanCost,
      totalPriceTerms
    } = financingData

    const replacements = {
      '%{amountToFinance}': formatFinancingNumber(financingData.amountToFinance),
      '%{cashPrice}': formatFinancingNumber(financingData.cashPrice),
      '%{casualtyInsuranceAmount}': formatFinancingNumber(casualtyInsuranceAmount),
      '%{commissionType}': getCommissionTypeText({isOpeningExpensesFinanced}),
      '%{contractDate}': dateSignup,
      '%{fee}': formatFinancingNumber(fee),
      '%{financedPrice}': formatFinancingNumber(financingData.financedPrice || financingData.cashPrice),
      '%{firstFee}': firstFee > 0 ? formatFinancingNumber(firstFee) : '',
      '%{firstInstallmentDate}': dateFirstInstallment,
      '%{lifeInsuranceAmount}': formatFinancingNumber(lifeInsuranceAmount),
      '%{insuranceRate}': formatFinancingNumber(insuranceRate),
      '%{nTerms}': terms - 1,
      '%{offerValidUntilDate}': dateOfferValidUntil,
      '%{openingExpenses}': formatFinancingNumber(openingExpenses),
      '%{openingFee}': formatFinancingNumber(openingFee || minEntry),
      '%{openingPercentage}': formatFinancingNumber(openingPercentage),
      '%{tae}': formatFinancingNumber(tae),
      '%{terms}': terms,
      '%{tin}': formatFinancingNumber(tin),
      '%{totalAmountDue}': formatFinancingNumber(totalAmountDue),
      '%{totalInsuranceAmount}': formatFinancingNumber(totalInsuranceAmount),
      '%{totalInterest}': formatFinancingNumber(totalInterest),
      '%{totalLoanAmount}': formatFinancingNumber(totalLoanAmount),
      '%{totalLoanAmountCost}': formatFinancingNumber(totalLoanCost),
      '%{totalLoanCost}': formatFinancingNumber(totalLoanCost),
      '%{totalPriceTerms}': formatFinancingNumber(totalPriceTerms),
      '%{offerEndDate}': dateOfferEnd
    }

    return html.replace(/%{\w+}/g, match => (replacements[match] === undefined ? '' : replacements[match]))
  }

  return (
    <section id={financingLegalConditionsSectionId} className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-header`}>
        <Headline1>{i18n.t(panelTitleLiteralKey)}</Headline1>
      </div>

      <div className={`${BASE_CLASS}-content`}>
        <div className={`${BASE_CLASS}-block`}>
          <Subhead1 as="h3">{i18n.t('COMMON.FINANCING_TC_ADEVINTA')}</Subhead1>
          <Caption>
            <MotorMarkdownWrapper documentName="adevinta" />
          </Caption>
        </div>

        {hasLenderLegalConditions && (
          <div className={`${BASE_CLASS}-block`}>
            <Subhead1 as="h3">{i18n.t(lenderLegalConditionsTitleLiteralKey)}</Subhead1>
            <Caption>
              <MotorMarkdownWrapper
                key={`${lenderLegalTextId}-${financingData.openingFee}-${financingData.terms}`}
                documentName={lenderLegalTextId}
                parser={markdownParser}
              />
            </Caption>
          </div>
        )}
      </div>
    </section>
  )
}

FinancingConditions.displayName = 'FinancingConditions'

FinancingConditions.propTypes = {
  financingData: PropTypes.shape({
    amountToFinance: PropTypes.number,
    cashPrice: PropTypes.number,
    financedPrice: PropTypes.number,
    lender: PropTypes.string,
    lenderLegalTextId: PropTypes.string,
    minEntry: PropTypes.number,
    openingFee: PropTypes.number,
    terms: PropTypes.number
  }),
  seller: PropTypes.shape({
    isDealer: PropTypes.bool,
    dealer: PropTypes.shape({
      pack: PropTypes.string
    })
  }),
  showC2cFinancing: PropTypes.bool
}

export default memo(FinancingConditions)
